import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { UserModel } from '../models/user.model';
import { UserApi } from '../api/user.api';
import { finalize, map, take, tap } from 'rxjs/operators';
import { BackendResponse, ToastDataV2, ToastV2Service } from '@schenck/rotec-components';
import { BehaviorSubject } from 'rxjs';
import { SpinnerService } from './spinner/spinner.service';
import { ServerErrorService } from './server-error/server-error.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly userApi = inject(UserApi);
  private readonly spinnerService = inject(SpinnerService);
  private readonly toastService = inject(ToastV2Service);
  private readonly serverErrorService = inject(ServerErrorService);

  private readonly _users$ = new BehaviorSubject<UserModel[]>(null);

  public fetchUsers(): void {
    this.spinnerService.setSpinnerState(true);
    this.userApi.users$
      .pipe(
        take(1),
        map((response) => {
          if (!BackendResponse.isSuccess(response)) {
            this.serverErrorService.setAppError(
              response.error?.message || (response.error as unknown as string) || true
            );
            return [];
          }

          return response.result?.data;
        }),
        finalize(() => this.spinnerService.setSpinnerState(false))
      )
      .subscribe((users) => this._users$.next(users));
  }

  public get users$(): Observable<UserModel[]> {
    return this._users$.asObservable();
  }

  public deleteUser$(user: UserModel): Observable<void> {
    this.spinnerService.setSpinnerState(true);

    return this.userApi.delete$(user.id).pipe(
      tap(() =>
        this.toastService.open(<ToastDataV2>{
          text: `USER_NAME was deleted successfully`,
          type: 'success',
          title: 'Success',
          textTranslateParams: { userName: `${user.givenName} ${user.surname}` },
        })
      ),
      finalize(() => this.spinnerService.setSpinnerState(false))
    );
  }

  public saveUser$(user: UserModel, edit = false): Observable<UserModel> {
    this.spinnerService.setSpinnerState(true);

    const response$: Observable<UserModel> = !edit
      ? this.userApi.create$(user)
      : this.userApi.update$(user);

    return response$.pipe(
      tap(() =>
        this.toastService.open(<ToastDataV2>{
          text: `USER_NAME was ${edit ? 'updated' : 'created'} successfully`,
          type: 'success',
          title: 'Success',
          textTranslateParams: { userName: user.givenName },
        })
      ),
      finalize(() => this.spinnerService.setSpinnerState(false))
    );
  }
}
